import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";

export default function Terms() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white relative" data-testid="terms-page">
      <BrandHeader />
      <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" aria-hidden />

      <main className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <Link
          to="/"
          data-testid="terms-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono-display text-zinc-500 hover:text-yellow-400 transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio
        </Link>

        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] font-mono-display text-yellow-400 mb-3">
            Legal · Documento vinculante
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-tight mb-3">
            Términos y Condiciones de Servicio
          </h1>
          <p className="text-sm text-zinc-500 font-mono-display">
            Última actualización: 24 de julio de 2026
          </p>
        </div>

        <div className="prose-block space-y-8 text-zinc-300 text-[15px] leading-relaxed">
          <p>
            Bienvenido a nuestro sitio web. Al acceder, registrarse o realizar cualquier transacción para la
            adquisición de gasolina u otros productos a través de nuestra plataforma, usted acepta cumplir y
            quedar sujeto a los siguientes Términos y Condiciones. Por favor, léalos detenidamente antes de
            utilizar nuestros servicios o procesar cualquier pago.
          </p>

          <Section n="1" title="Objeto y Alcance">
            <p>
              Los presentes Términos y Condiciones regulan el uso del sitio web, la comercialización de
              combustible (gasolina), así como las políticas de procesamiento de pagos, entregas, cancelaciones
              y devoluciones aplicables a todas las transacciones realizadas por los usuarios dentro de la
              plataforma.
            </p>
          </Section>

          <Section n="2" title="Capacidad Legal y Registro de Usuario">
            <Item label="Edad mínima">
              Para realizar compras en este sitio web, el usuario debe ser mayor de 18 años y contar con plena
              capacidad legal para contratar.
            </Item>
            <Item label="Información veraz">
              El usuario es responsable de proporcionar información exacta, verídica y actualizada en su
              cuenta y durante el proceso de pago (nombre, datos de contacto, información fiscal y de
              facturación).
            </Item>
          </Section>

          <Section n="3" title="Precios, Impuestos y Disponibilidad">
            <Item label="Modificación de precios">
              Todos los precios de los combustibles y productos publicados en la plataforma están sujetos a
              cambios sin previo aviso debido a la fluctuación del mercado, regulaciones gubernamentales o
              normativas locales.
            </Item>
            <Item label="Impuestos aplicables">
              Los precios mostrados pueden o no incluir los impuestos locales, estatales o federales
              aplicables, los cuales se detallarán claramente antes de confirmar y finalizar el pago.
            </Item>
            <Item label="Disponibilidad">
              La venta de gasolina está sujeta a la disponibilidad de inventario en el punto de despacho o
              distribución correspondiente.
            </Item>
          </Section>

          <Section n="4" title="Procesamiento de Pagos y Seguridad">
            <Item label="Métodos de pago">
              Aceptamos las formas de pago indicadas en la plataforma (tarjetas de crédito/débito,
              transferencias o pasarelas de pago autorizadas). Al proporcionar los datos de pago, el usuario
              garantiza que está autorizado para utilizarlos.
            </Item>
            <Item label="Autorización de cargos">
              El usuario autoriza expresamente a nuestra plataforma a realizar el cargo correspondiente al
              monto total de la orden (incluyendo tarifas e impuestos) en el método de pago seleccionado.
            </Item>
            <Item label="Seguridad de las transacciones">
              Las transacciones financieras se procesan a través de pasarelas de pago externas y seguras que
              cumplen con los estándares de cifrado de la industria (como PCI-DSS). Nuestra plataforma no
              almacena información sensible de tarjetas de crédito o datos bancarios en sus servidores
              principales.
            </Item>
            <Item label="Retenciones y Verificaciones">
              Nos reservamos el derecho de denegar, cancelar o suspender cualquier transacción o cuenta si se
              sospecha de fraude, suplantación de identidad o uso no autorizado de medios de pago.
            </Item>
          </Section>

          <Section n="5" title="Políticas de Cancelación, Reembolsos y Devoluciones">
            <Item label="Naturaleza del producto">
              Debido a la naturaleza inflamable, peligrosa y perecedera de los combustibles, todas las ventas
              de gasolina son finales una vez despachado o entregado el producto, salvo que la ley aplicable
              exija lo contrario.
            </Item>
            <Item label="Cancelaciones por parte del usuario">
              Las órdenes podrán ser canceladas únicamente antes de que el proceso de despacho o entrega haya
              iniciado. Si la cancelación procede, el reembolso se efectuará a través del mismo medio de pago
              original, sujeto a los tiempos de procesamiento de la entidad bancaria o pasarela.
            </Item>
            <Item label="Fallas en el pago o transacciones duplicadas">
              En caso de cargos duplicados, errores técnicos atribuibles al sistema o pagos procesados por
              error debido a fallas de la plataforma, el usuario deberá notificarlo dentro de un plazo máximo
              de [Número] días adjuntando los comprobantes correspondientes para proceder con la revisión y
              el reembolso si procede.
            </Item>
          </Section>

          <Section n="6" title="Limitación de Responsabilidad">
            <Item label="Uso del sitio">
              La plataforma se proporciona &quot;tal cual&quot; y &quot;según disponibilidad&quot;. No garantizamos que el
              sitio web esté libre de errores, interrupciones o fallas técnicas temporales.
            </Item>
            <Item label="Fuerza Mayor">
              No seremos responsables por retrasos, incumplimientos o daños derivados de causas fuera de
              nuestro control razonable, incluyendo desastres naturales, fallas en la red eléctrica,
              restricciones gubernamentales, escasez de combustible, huelgas o fallas en las pasarelas de
              pago de terceros.
            </Item>
            <Item label="Manejo del producto">
              Nuestra responsabilidad sobre el combustible finaliza en el momento en que se efectúa la
              entrega o el despacho al usuario o al vehículo autorizado. No nos hacemos responsables por el
              mal almacenamiento, manipulación incorrecta o uso indebido del combustible posterior a su
              entrega.
            </Item>
          </Section>

          <Section n="7" title="Modificaciones a los Términos">
            <p>
              Nos reservamos el derecho absoluto de modificar, actualizar o cambiar estos Términos y
              Condiciones en cualquier momento. Las modificaciones entrarán en vigor de forma inmediata tras
              su publicación en el sitio web. El uso continuado de la plataforma después de dichos cambios
              constituirá la aceptación de los mismos.
            </p>
          </Section>

          <Section n="8" title="Legislación Aplicable y Jurisdicción">
            <p>
              Estos Términos y Condiciones se rigen e interpretan de acuerdo con las leyes aplicables en la
              jurisdicción donde opera legalmente la empresa. Cualquier disputa, controversia o reclamación
              derivada de la interpretación o ejecución de este contrato se someterá a la jurisdicción
              exclusiva de los tribunales competentes de dicha localidad.
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 text-xs text-zinc-500 font-mono-display tracking-widest text-center">
          © {new Date().getFullYear()} · PORTAL GASOLINA B94 · TODOS LOS DERECHOS RESERVADOS
        </div>
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="border-l-2 border-yellow-400/40 pl-5 md:pl-6">
      <h2 className="font-display font-bold text-xl md:text-2xl text-white mb-4 flex items-baseline gap-3">
        <span className="text-yellow-400 font-mono-display text-sm tracking-widest">{n}.</span>
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Item({ label, children }) {
  return (
    <p>
      <span className="font-semibold text-white">{label}:</span>{" "}
      <span className="text-zinc-400">{children}</span>
    </p>
  );
}
